import { Vector3 } from 'three';
import type { HalfedgeDS } from '../core/HalfedgeDS';
import type { Halfedge } from '../core/Halfedge';
import type { Vertex } from '../core/Vertex';
import { cutEdge } from '../operations/cutEdge';

export type EdgeIntersection = {
  halfEdge: Halfedge;
  point: Vector3;
};

export type MultiCutOptions = {
  /** toleration for merging intersections */
  epsT?: number;
  /** tolerance for cutEdge (snap to vertices) */
  cutTolerance?: number;
  /** ignore hits extremely close to the ends of the edge (0 and 1) before calling cutEdge */
  discardEndpointHits?: boolean;
};

export type MultiCutResult = {
  /** newly created vertices organized by groups (one for each edge) */
  createdByEdge: Map<string, Vertex[]>;
  /** flat list of all created vertices */
  createdAll: Vertex[];
};

/**
 * Safely performs multiple cuts on edges:
 * 1) groups intersections by original edge (independent of orientation),
 * 2) computes parameter t along original A→B, sorts and merges close intersections,
 * 3) cuts sequentially; after each cut, continues on segment V→B (original B).
 *
 * NOTE: `cutEdge` is allowed to modify topology and maps; this function builds on that
 *        by properly "shifting" the current segment.
 */
export function cutEdges(ds: HalfedgeDS, edgeIntersections: EdgeIntersection[], opts: MultiCutOptions = {}): MultiCutResult {
  const { epsT = 1e-6, cutTolerance = 1e-10, discardEndpointHits = true } = opts;

  // 1) grouping intersections by stable edge key (independent of orientation)
  //    key: "minId_maxId"
  type CutExt = EdgeIntersection & {
    aId: number;
    bId: number; // original endpoint ids (sorted)
    aPos: Vector3;
    bPos: Vector3; // position at the time of grouping
    t: number; // parameter on A→B
    orientedHe: Halfedge; // he oriented A(minId) → B(maxId)
  };

  const groups = new Map<string, CutExt[]>();

  for (const h of edgeIntersections) {
    // original edge endpoints (IDs)
    const idA = h.halfEdge.vertex.id;
    const idB = h.halfEdge.twin.vertex.id;
    const minId = Math.min(idA, idB);
    const maxId = Math.max(idA, idB);
    const key = `${minId}_${maxId}`;

    // take halfedge oriented from minId → maxId
    const oriented = h.halfEdge.vertex.id === minId ? h.halfEdge : h.halfEdge.twin.vertex.id === minId ? h.halfEdge.twin : h.halfEdge; // fallback (should not happen)

    const Apos = oriented.vertex.position.clone();
    const Bpos = oriented.twin.vertex.position.clone();

    // parameter t = projection of point onto original segment A→B
    const AB = Bpos.clone().sub(Apos);
    const AP = h.point.clone().sub(Apos);
    const denom = Math.max(AB.lengthSq(), 1e-18);
    let t = AB.dot(AP) / denom;

    if (discardEndpointHits) {
      if (t <= epsT || t >= 1 - epsT) continue; // too close to ends
    }

    t = Math.max(0, Math.min(1, t));

    const ext: CutExt = {
      ...h,
      aId: minId,
      bId: maxId,
      aPos: Apos,
      bPos: Bpos,
      t,
      orientedHe: oriented,
    };

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ext);
  }

  // 2) for each edge: sort t, merge close ones, cut sequentially
  const createdByEdge = new Map<string, Vertex[]>();
  const createdAll: Vertex[] = [];

  for (const [key, arr] of groups) {
    if (arr.length === 0) continue;

    // sort by t
    arr.sort((u, v) => u.t - v.t);

    // deduplication by epsT
    const uniq: CutExt[] = [];
    for (let i = 0; i < arr.length; i++) {
      if (i === 0 || Math.abs(arr[i].t - arr[i - 1].t) > epsT) {
        uniq.push(arr[i]);
      }
    }
    if (uniq.length === 0) continue;

    // initial oriented halfedge A(minId) → B(maxId)
    let current = uniq[0].orientedHe;

    // B (original) — vertex by ID (must exist throughout cuts)
    const origB = current.twin.vertex; // because current is A→B

    const madeHere: Vertex[] = [];

    for (const hit of uniq) {
      // cut on current segment
      const { vertex: V } = cutEdge(ds, current, hit.point, cutTolerance);
      madeHere.push(V);
      createdAll.push(V);

      // next segment: V → original B
      const nextHe = V.getHalfedgeToVertex(origB);
      if (!nextHe) {
        // If this happens, there is a problem with the topology – quick diagnostics:
        throw new Error(`After cut, cannot find halfedge from new vertex ${V.id} to original B ${origB.id} on edge ${key}`);
      }
      current = nextHe;
    }

    createdByEdge.set(key, madeHere);
  }

  return { createdByEdge, createdAll };
}
